import { useEffect, useRef } from "react";
import { getToken, clearToken, fetchAuthUser } from "../api/auth";
import { useSessionExpiryStore } from "./useSessionExpiryStore";
import { LOGIN_URL } from "../config/api";

const CHECK_INTERVAL = 60000; // Check every 60 seconds
const WARN_YELLOW_MS = 5 * 60 * 1000; // 5 minutes before expiry
const WARN_RED_MS = 90 * 1000; // 1.5 minutes before expiry

/**
 * useAuthCheck Hook
 *
 * Checks if the authentication token is expired by comparing
 * the expTime from GetAuthUserDetails API with current time.
 * If expired, clears token and redirects to login page.
 *
 * Also schedules session expiry warnings at 5 min and 1.5 min remaining.
 *
 * Runs on mount and periodically every 60 seconds.
 * Only runs AFTER initial authentication is complete.
 */
function useAuthCheck() {
  const hasCheckedOnce = useRef(false);
  const warningScheduledRef = useRef(false);
  const yellowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const redTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (yellowTimerRef.current) {
      clearTimeout(yellowTimerRef.current);
      yellowTimerRef.current = null;
    }
    if (redTimerRef.current) {
      clearTimeout(redTimerRef.current);
      redTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };

  useEffect(() => {
    const checkTokenExpiration = async () => {
      const token = getToken();

      if (!token) {
        // Only redirect if we've successfully checked at least once
        // This prevents redirect during initial auth flow
        if (hasCheckedOnce.current) {
          console.warn("[useAuthCheck] No token found, redirecting to login");
          clearTimers();
          useSessionExpiryStore.getState().clear();
          localStorage.clear();
          window.location.href = LOGIN_URL;
        }
        return;
      }

      try {
        const data = await fetchAuthUser();

        // Mark that we've successfully checked once
        hasCheckedOnce.current = true;

        // Check if expTime exists
        if (!data.expTime) {
          console.warn("[useAuthCheck] No expTime in response");
          return;
        }

        // Parse expTime (format: "2025-11-04 19:01:06")
        // Replace space with 'T' and add 'Z' to make it ISO 8601 UTC format
        const isoFormat = data.expTime.replace(" ", "T") + "Z";
        const expTimeUTC = new Date(isoFormat).getTime();
        const currentTimeUTC = Date.now(); // Already in UTC milliseconds

        console.debug(
          `[useAuthCheck] Server expTime: "${data.expTime}" → Parsed as UTC: ${new Date(expTimeUTC).toISOString()}, Current UTC: ${new Date(currentTimeUTC).toISOString()}`,
        );

        // If token is expired
        if (expTimeUTC < currentTimeUTC) {
          console.warn(
            `[useAuthCheck] Token expired! Exp: ${new Date(expTimeUTC).toISOString()}, Now: ${new Date(currentTimeUTC).toISOString()}`,
          );
          clearTimers();
          useSessionExpiryStore.getState().clear();
          clearToken();
          localStorage.clear();
          window.location.href = LOGIN_URL;
          return;
        }

        const msRemaining = expTimeUTC - currentTimeUTC;
        const minutesRemaining = Math.floor(msRemaining / 1000 / 60);
        console.debug(
          `[useAuthCheck] Token valid for ${minutesRemaining} more minutes`,
        );

        // Schedule warning banners and precise logout only once per session
        if (!warningScheduledRef.current) {
          warningScheduledRef.current = true;

          const msUntilYellow = msRemaining - WARN_YELLOW_MS;
          const msUntilRed = msRemaining - WARN_RED_MS;

          // Precise logout at exact expiry time
          logoutTimerRef.current = setTimeout(() => {
            useSessionExpiryStore.getState().clear();
            clearToken();
            localStorage.clear();
            window.location.href = LOGIN_URL;
          }, msRemaining + 500);

          // Red warning (1.5 min)
          if (msUntilRed > 0) {
            redTimerRef.current = setTimeout(() => {
              useSessionExpiryStore.getState().setWarning("red", expTimeUTC);
            }, msUntilRed);
          } else if (msRemaining > 0) {
            useSessionExpiryStore.getState().setWarning("red", expTimeUTC);
          }

          // Yellow warning (5 min)
          if (msUntilYellow > 0) {
            yellowTimerRef.current = setTimeout(() => {
              useSessionExpiryStore.getState().setWarning("yellow", expTimeUTC);
            }, msUntilYellow);
          } else if (msUntilRed > 0) {
            useSessionExpiryStore.getState().setWarning("yellow", expTimeUTC);
          }
        }
      } catch (error) {
        console.error("[useAuthCheck] Error checking token expiration:", error);
        // Don't redirect on network errors, just log
      }
    };

    // Check immediately on mount
    checkTokenExpiration();

    // Set up periodic checking every 60 seconds
    const intervalId = setInterval(checkTokenExpiration, CHECK_INTERVAL);

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useAuthCheck;
